/**
 * Stores ranked by how dense their personal data is (release 8, C5; capability
 * rows 30 and 45).
 *
 * The risk ranking says which store is most exposed. This says which is most
 * FULL of personal data: the share of its catalogued columns that hold it, how
 * many data points that is, and how wide. The same list sorts three ways
 * because the answers differ (a small lookup table can be all personal data;
 * a huge ledger can hold a few personal columns across millions of rows).
 *
 * A store with no column catalogue (a file share, a bucket) cannot be given a
 * density. It is counted under the list, never dropped and never given a zero.
 *
 * The bars are length only, in a neutral colour: density is not severity, and
 * the brand colour is for actions.
 */

import { useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import { ErrorState, LoadingState } from '@foctta/ui-shell';

import { Button } from '@/components/ui/button';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getStoreDensity, type DenseStore, type DensityReport, type DensitySort } from '@/lib/density-api';

import { BuFilterSelect } from './fleet/BuFilterSelect';

const INDIAN = new Intl.NumberFormat('en-IN');

export const SORT_LABEL: Record<DensitySort, string> = {
  density: 'Density (share of columns)',
  points: 'Data points (rows x columns)',
  columns: 'Personal-data columns',
};

/** 0.5 -> "50%"; 0.0333 -> "3.3%"; never "0%" for a store that holds some. */
export function pct(density: number): string {
  if (density >= 0.995) return '100%';
  const p = density * 100;
  if (p > 0 && p < 0.1) return '<0.1%';
  return `${p >= 10 ? Math.round(p) : Math.round(p * 10) / 10}%`;
}

/** 4,000 -> "4,000"; 250,000 -> "2.5 lakh"; 18,000,000 -> "1.8 crore" (Indian units for large counts). */
export function points(n: number | null): string {
  if (n === null) return 'rows not known';
  const one = (x: number) => (Math.round(x * 10) / 10).toString();
  if (n >= 10_000_000) return `${one(n / 10_000_000)} crore`;
  if (n >= 100_000) return `${one(n / 100_000)} lakh`;
  return INDIAN.format(n);
}

/** "Across 6 catalogued stores, 22% of columns hold personal data." The denominator is every catalogued store. */
export function overallLine(r: DensityReport): string {
  const t = r.totals;
  if (t.overallDensity === null) return '';
  return `Across ${INDIAN.format(t.cataloguedStores)} catalogued store${t.cataloguedStores === 1 ? '' : 's'}, ${pct(t.overallDensity)} of all columns hold personal data.`;
}

export function footnote(r: DensityReport): string {
  const t = r.totals;
  const parts: string[] = [];
  if (t.measuredStores === 0) {
    parts.push('No catalogued store holds personal data yet, so none can be ranked.');
  }
  if (t.unlabelledStores > 0) {
    parts.push(
      `${INDIAN.format(t.unlabelledStores)} store${t.unlabelledStores === 1 ? '' : 's'} hold${t.unlabelledStores === 1 ? 's' : ''} personal data but no catalogued column carries a label; ${t.unlabelledStores === 1 ? 'it is' : 'they are'} listed at 0%.`,
    );
  }
  if (t.unmeasuredStores > 0) {
    parts.push(
      `${INDIAN.format(t.unmeasuredStores)} store${t.unmeasuredStores === 1 ? '' : 's'} hold${t.unmeasuredStores === 1 ? 's' : ''} personal data but ${t.unmeasuredStores === 1 ? 'has' : 'have'} no column catalogue (file shares, buckets), so ${t.unmeasuredStores === 1 ? 'it cannot' : 'they cannot'} be ranked here.`,
    );
  }
  return parts.join(' ');
}

function Row({ s, onOpen }: { s: DenseStore; onOpen?: ((assetId: string) => void) | undefined }) {
  return (
    <li className="flex items-center gap-3 py-2 border-t border-border/30 first:border-t-0" data-testid="density-row">
      <span className="w-6 text-sm tabular-nums text-muted-foreground text-right">{s.rank}</span>
      <div className="min-w-0 flex-1">
        {onOpen ? (
          <button
            type="button"
            onClick={() => onOpen(s.assetId)}
            className="text-sm font-medium truncate max-w-full text-left hover:underline"
          >
            {s.name}
          </button>
        ) : (
          <div className="text-sm font-medium truncate">{s.name}</div>
        )}
        <div className="text-xs text-muted-foreground truncate">
          {s.sourceType} - {s.businessUnitId === null ? 'Unassigned' : (s.businessUnitName ?? 'Not visible to you')}
          {s.unlabelled
            ? ' - personal data found, no catalogued column labelled'
            : s.classCount > 0
              ? ` - ${s.classCount} kind${s.classCount === 1 ? '' : 's'} of personal data`
              : ''}
        </div>
      </div>
      <div className="w-40 shrink-0">
        <div
          className="h-2 rounded-full bg-muted overflow-hidden"
          role="img"
          aria-label={`${pct(s.density)} of columns hold personal data`}
        >
          <div className="h-full bg-muted-foreground/70" style={{ width: `${s.density === 0 ? 0 : Math.max(2, Math.round(s.density * 100))}%` }} />
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">
          {pct(s.density)} - {s.personalColumns} of {s.columnsKnown} columns
        </div>
      </div>
      <div className="w-28 shrink-0 text-right text-sm tabular-nums" title="Rows x personal-data columns">
        {points(s.dataPoints)}
        <div className="text-xs text-muted-foreground">{s.dataPoints === null ? '' : 'data points'}</div>
      </div>
    </li>
  );
}

export const TOP = 10;
export const ALL = 200;

export function StoreDensityCard({
  bu: buProp,
  onOpen,
}: {
  bu?: string;
  /** Open the store in the page's detail drawer. */
  onOpen?: (assetId: string) => void;
} = {}) {
  const [sort, setSort] = useState<DensitySort>('density');
  const [bu, setBu] = useState<string>(buProp ?? '');
  const [showAll, setShowAll] = useState(false);
  const [report, setReport] = useState<DensityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => setBu(buProp ?? ''), [buProp]);

  useEffect(() => {
    let live = true;
    setReport(null);
    setError(null);
    getStoreDensity({ sort, limit: showAll ? ALL : TOP, ...(bu ? { bu } : {}) }).then(
      (r) => {
        if (live) setReport(r);
      },
      (e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : 'Could not load the density ranking');
      },
    );
    return () => {
      live = false;
    };
  }, [sort, bu, showAll, attempt]);

  return (
    <GlassCard className="p-5 mb-6" data-testid="store-density-card">
      <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4 text-muted-foreground" aria-hidden="true" /> Where personal data is densest
          </CardTitle>
          <CardDescription className="text-xs">
            Stores ranked by the share of their columns that hold personal data. Start minimising at the top.
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 shrink-0">
          <BuFilterSelect id="density-bu" value={bu} onChange={(v) => { setBu(v); setShowAll(false); }} />
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground" htmlFor="density-sort">
              Sort by
            </label>
            <select
              id="density-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as DensitySort)}
              className="glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
            >
              {(Object.keys(SORT_LABEL) as DensitySort[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CardHeader>

      {error && <ErrorState title="Could not load the density ranking" detail={error} onRetry={() => setAttempt((n) => n + 1)} />}
      {!error && !report && <LoadingState caption="Measuring stores..." />}

      {report && (
        <>
          {report.items.length > 0 && (
            <>
              {overallLine(report) && (
                <p className="text-sm mb-2" data-testid="density-summary">
                  {overallLine(report)}
                </p>
              )}
              <ul>
                {report.items.map((s) => (
                  <Row key={s.assetId} s={s} onOpen={onOpen} />
                ))}
              </ul>
            </>
          )}
          {report.truncated && (
            <p className="mt-2 text-xs text-muted-foreground flex flex-wrap items-center gap-2">
              Showing {report.items.length} of {INDIAN.format(report.totals.measuredStores)} stores.
              {!showAll && (
                <Button variant="glass" size="sm" onClick={() => setShowAll(true)}>
                  Show all
                </Button>
              )}
            </p>
          )}
          {footnote(report) && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="density-footnote">
              {footnote(report)}
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">{report.basis}</p>
        </>
      )}
    </GlassCard>
  );
}
